import { useState, useRef, useEffect } from 'react'
import { EnhancedVideoPlayer } from '@/components/EnhancedVideoPlayer'
import { Toolbar } from '@/components/Toolbar'
import { StatusBar } from '@/components/StatusBar'
import { VideoLibrarySidebar, VideoLibraryDrawer } from '@/components/VideoLibrarySidebar'
import { VideoToGifDialog } from '@/components/VideoToGifDialog'
import { Button } from '@/components/ui/button'
import { Wand2 } from 'lucide-react'
import { detectVideoFormat, VideoFormat } from '@/lib/utils'
import { track } from '@/lib/track'
import { useLocation, useNavigate } from 'react-router-dom'
import { useVideoWithAudit } from '@/features/videos/use-video-audit'
import { useCloudVideos } from '@/features/videos/use-cloud-videos'
import { OnboardingTour } from '@/components/OnboardingTour'
import { useOnboardingTour } from '@/hooks/use-onboarding-tour'
import type { Video } from '@/lib/local-types'
import { toast } from 'sonner'

interface ExtractorAppProps {
  embedded?: boolean
}

export function ExtractorApp({ embedded = false }: ExtractorAppProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const [videoSrc, setVideoSrc] = useState<string | null>(null)
  const [videoName, setVideoName] = useState<string | null>(null)
  const [currentFrame, setCurrentFrame] = useState(0)
  const [videoFormat, setVideoFormat] = useState<VideoFormat | null>(null)
  const [videoFileSize, setVideoFileSize] = useState<number | null>(null)
  const [videoId, setVideoId] = useState<string | null>(null)
  const [initialFrame, setInitialFrame] = useState<number | undefined>(undefined)
  const [isUploading, setIsUploading] = useState(false)
  const [isQuickGifOpen, setIsQuickGifOpen] = useState(false)
  const { logVideoAction } = useVideoWithAudit()
  const { videos, isLoading: isLoadingVideos, uploadVideo, getVideo, canUpload, storageInfo, refresh: _refreshVideos } = useCloudVideos()
  const { showTour, completeTour, skipTour } = useOnboardingTour()
  
  // These refs will be passed to VideoPlayer to get access to the actual video and canvas elements
  const videoRef = useRef<HTMLVideoElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  
  // Check for video ID in query params (for library integration)
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const videoIdParam = params.get('video')
    const frameParam = params.get('frame')
    
    if (videoIdParam) {
      // Load the video from this browser's local library.
      getVideo(videoIdParam).then((video) => {
        if (video && video.url) {
          setVideoSrc(video.url)
          setVideoName(video.name)
          setVideoFileSize(video.size)
          setVideoId(video.id)
          setCurrentFrame(0)
          
          // Set initial frame if provided
          if (frameParam) {
            const frameNum = parseInt(frameParam, 10)
            if (!isNaN(frameNum) && frameNum > 0) {
              setInitialFrame(frameNum)
            }
          }
          
          // Log the video loaded from library
          logVideoAction('video_played', `Video loaded from library: ${video.name}`, {
            videoId: video.id,
            videoName: video.name,
            videoSize: video.size
          }, { toast: false })
        } else {
          console.warn('Could not load video from library - The video may have been removed.')
          toast.error('Video not found', { description: 'The video may have been deleted.' })
        }
      }).catch((error) => {
        console.error('Error loading video:', error)
        toast.error('Failed to load video')
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search])
  
  // Handle file input change for video upload
  const handleOpenVideo = async (file: File) => {
    // Create object URL for immediate preview
    const url = URL.createObjectURL(file)
    setVideoSrc(url)
    setVideoName(file.name)
    setCurrentFrame(0)
    
    // Store format information
    const format = detectVideoFormat(file)
    setVideoFormat(format)
    setVideoFileSize(file.size)
    track('opened-video', format ? format.format : 'unknown', file.size)

    if (!canUpload(file.size)) {
      toast.error('Local storage limit reached', {
        description: 'Remove a few saved videos before adding this one.'
      })
      return
    }

    setIsUploading(true)
    const uploadToast = toast.loading('Saving video locally...')

    try {
      const video = await uploadVideo(file, (progress) => {
        toast.loading(progress < 100 ? `Saving... ${progress}%` : 'Finishing...', { id: uploadToast })
      })

      if (video) {
        setVideoId(video.id)
        if (video.url) {
          URL.revokeObjectURL(url)
          setVideoSrc(video.url)
        }

        toast.success('Video saved locally', {
          id: uploadToast,
          description: 'It will be available from this browser on your next visit.'
        })

        logVideoAction('video_uploaded', `Video saved: ${file.name}`, {
          videoId: video.id,
          videoName: file.name,
          videoSize: file.size,
        }, {
          toast: false,
          additionalDetails: {
            fileType: file.type,
            format: format ? format.format : 'unknown'
          }
        })
      }
    } catch (error) {
      console.error('Error saving video:', error)
      toast.error('Failed to save video', {
        id: uploadToast,
        description: error instanceof Error ? error.message : 'Please try again'
      })
    } finally {
      setIsUploading(false)
    }
  }
  
  // Handle quit
  const handleQuit = () => {
    // In a real app, this would close the application
    // In our browser context, we'll just show a message
    if (confirm('Are you sure you want to quit?')) {
      window.close()
      // If window.close() is blocked by the browser (common in most browsers)
      console.log('To fully close the application, please close this browser tab.')
    }
  }
  
  // Initialize state from localStorage
  useEffect(() => {
    // Log File System Access API support status
    if ('showDirectoryPicker' in window) {
      console.log('✅ File System Access API supported - Users can select save folders')
    } else {
      console.log('ℹ️ File System Access API not supported - Using download fallback')
    }
    
    return () => {
      if (videoSrc) {
        URL.revokeObjectURL(videoSrc)
      }
    }
  }, [videoSrc])

  // Handle selecting a video from the sidebar
  const handleSelectVideo = async (video: Video) => {
    // Update URL without full navigation
    navigate(`/?view=studio&video=${video.id}`, { replace: true })
    
    // Load the video
    if (video.url) {
      setVideoSrc(video.url)
      setVideoName(video.name)
      setVideoFileSize(video.size)
      setVideoId(video.id)
      setCurrentFrame(0)
      setInitialFrame(undefined)
      
      logVideoAction('video_played', `Video selected from sidebar: ${video.name}`, {
        videoId: video.id,
        videoName: video.name,
        videoSize: video.size
      }, { toast: false })
    } else {
      // Need to fetch the video URL
      const fullVideo = await getVideo(video.id)
      if (fullVideo && fullVideo.url) {
        setVideoSrc(fullVideo.url)
        setVideoName(fullVideo.name)
        setVideoFileSize(fullVideo.size)
        setVideoId(fullVideo.id)
        setCurrentFrame(0)
        setInitialFrame(undefined)
        
        logVideoAction('video_played', `Video selected from sidebar: ${fullVideo.name}`, {
          videoId: fullVideo.id,
          videoName: fullVideo.name,
          videoSize: fullVideo.size
        }, { toast: false })
      }
    }
  }

  // Handle triggering the file upload
  const handleTriggerUpload = () => {
    // Trigger the toolbar's open video functionality
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'video/*'
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0]
      if (file) {
        handleOpenVideo(file)
      }
    }
    input.click()
  }

  return (
    <div className={`flex flex-col bg-background text-foreground ${embedded ? 'h-[calc(100vh-126px)] min-h-[620px]' : 'h-screen'}`}>
      {/* Onboarding Tour */}
      {showTour && (
        <OnboardingTour onComplete={completeTour} onSkip={skipTour} />
      )}
      
      <Toolbar 
        onOpenVideo={handleOpenVideo}
        onQuit={handleQuit}
        disabled={isUploading}
      />
      
      <div className="flex flex-1 overflow-hidden">
        {/* Video Library, permanent docked panel (desktop) */}
        <VideoLibrarySidebar
          videos={videos}
          isLoading={isLoadingVideos}
          currentVideoId={videoId}
          storageInfo={storageInfo}
          onSelectVideo={handleSelectVideo}
          onUploadVideo={handleTriggerUpload}
        />

        <main className="flex-1 min-w-0 overflow-auto p-2 md:p-4">
          <div className="container mx-auto max-w-4xl">
            {/* Mobile: open the video library drawer (docked panel is desktop-only) */}
            <div className="mb-3 md:hidden">
              <VideoLibraryDrawer
                videos={videos}
                isLoading={isLoadingVideos}
                currentVideoId={videoId}
                storageInfo={storageInfo}
                onSelectVideo={handleSelectVideo}
                onUploadVideo={handleTriggerUpload}
              />
            </div>
            {videoSrc && (
              <div className="mb-3 flex justify-end">
                <Button
                  size="sm"
                  onClick={() => setIsQuickGifOpen(true)}
                  className="gap-2 bg-gradient-to-r from-primary to-primary/80"
                >
                  <Wand2 className="h-4 w-4" />
                  Quick GIF
                </Button>
              </div>
            )}
            <EnhancedVideoPlayer 
              videoSrc={videoSrc} 
              videoFormat={videoFormat}
              videoFileSize={videoFileSize}
              onFrameChange={setCurrentFrame}
              videoRef={videoRef}
              canvasRef={canvasRef}
              videoName={videoName}
              videoId={videoId}
              initialFrame={initialFrame}
            />
          </div>
        </main>
      </div>
      
      <StatusBar 
        videoName={videoName}
        currentFrame={currentFrame}
        videoId={videoId}
      />

      <VideoToGifDialog
        open={isQuickGifOpen}
        onOpenChange={setIsQuickGifOpen}
        videoSrc={videoSrc}
        videoName={videoName}
      />
    </div>
  )
}